import {
  defaultRepairTrade,
  isRepairTrade,
  planRepair,
  DEFAULT_JOB_OVERHEAD_PCT,
  DEFAULT_MATERIAL_TAX_RATE,
  DEFAULT_WORK_TAX_RATE,
  contractBaseForMargin,
  defaultColorFor,
  estimateLineDescription,
  PriceBook,
  TARGET_MARGIN,
  generatePlan,
  fitFramingNails,
  generationRecord,
  generationSnapshot,
  withAreas,
  rateFromTotal,
  splitCents,
  squaresWithWaste,
  toDecimal,
  toCents,
  todayCentral,
  ROOF_PATHS,
  templateIsUnbuilt,
  templatesForPath,
  supplierForTemplate,
  suppliersByPath,
  type RoofPath,
  type SuppliersChosen,
  UNBUILT_TEMPLATE_EXPLANATION,
  UNBUILT_TEMPLATE_LABEL,
  type ColorProduct,
  type GeneratorProduct,
  type Job,
  type PricedSupplier,
  type Product,
  type ProductPrice,
  type RoofLayout,
  type RoofTemplate,
  type RoofTemplateItem,
  type Supplier,
  deckSquareFeet,
  defaultDeckAnswers,
  defaultExposedFastenerAnswers,
  defaultFlatRoofAnswers,
  defaultSidingAnswers,
  flatRoofScopeOfWork,
  flatSquaresOf,
  isDeckType,
  isExposedFastenerType,
  isFlatRoofType,
  isSidingType,
  LABOR_ONLY_SCOPE_HEADING,
  laborOnlyName,
  laborOnlyScope,
  linesToPlan,
  sidingLineColor,
  sidingPanelProductIds,
  sidingSquaresOf,
  sidingSquaresWithWaste,
  roofsWithout,
  sectionIsLive,
  sectionRoof,
  sectionWarnings,
  type Cents,
  type DeckAnswers,
  type ExposedFastenerAnswers,
  type GenerationRecord,
  type GenerationSnapshot,
  type TemplateAnswers,
  type FlatRoofAnswers,
  type RoofMeasurements,
  type RoofSection,
  type SidingAnswers,
  type WallMeasurement,
} from "@roof-hawk/core";
import { useState } from "react";

import { Modal, Spinner } from "../../components/ui";
import { t } from "../../i18n";
import { fetchFramingNails } from "../../lib/framingNails";
import { useAsync } from "../../lib/hooks";
import {
  fetchEstimateChildren,
  fetchRoofLayouts,
  insertRow,
  updateRow,
} from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";
import { FlatRoofQuestions } from "./FlatRoofQuestions";
import { MetalPanelQuestions } from "./MetalPanelQuestions";
import { PATH_CLASS } from "./paths";
import { RoofSectionsField } from "./RoofSectionsField";
import { DeckQuestions } from "./DeckQuestions";
import { SidingQuestions } from "./SidingQuestions";

/** A roof a part is generated against: a measured layout, or a piece of one. */
type PartRoof = RoofMeasurements & { name?: string | null };

/** What a written part hands back, so the estimate can be priced in one go. */
interface WrittenPart {
  /** The line the customer reads, waiting for its share of the price. */
  lineId: string;
  /** Its quantity, in squares. */
  qty: number;
  /** What this part costs us, in cents. Its share of the contract. */
  cost: number;
  /** What to call it in a warning — the package, and the area if it is one. */
  label: string;
  skipped: { name: string; reason: string }[];
  warnings: string[];
}

/**
 * The squares a part's headline line is measured in.
 *
 * Pitched roof where there is one, and the flat area where there is not: a TPO
 * roof has no pitched squares at all, and heading it "1 SQ" — which is what a
 * missing measurement falls back to — prices the whole membrane as one square.
 */
function headlineSquares(roofs: readonly PartRoof[]): number {
  const pitched = squaresWithWaste(roofs);
  if (pitched > 0) return pitched;
  const flat = roofs.reduce((total, roof) => {
    const area = flatSquaresOf(roof);
    if (!Number.isFinite(area) || area <= 0) return total;
    const waste = Number(roof.waste_pct ?? 0);
    return total + area * (1 + (Number.isFinite(waste) ? waste : 0) / 100);
  }, 0);
  if (flat > 0) return flat;
  // And a wall: a siding part is measured in squares of siding and trim,
  // with the wall's own waste factor, the same way the panels are bought.
  return sidingSquaresWithWaste(roofs);
}

/**
 * What a package is missing before it can be generated, or null.
 *
 * A roof package needs a measured roof; a siding package, measured walls; a
 * deck, only the size typed into its questions.
 */
function missingMeasurements(
  chosen: readonly { roof_type?: string | null }[],
  hasRoof: boolean,
  hasWalls: boolean,
  hasDeckSize: boolean,
): string | null {
  const onRoof = chosen.some(
    (t) => !isSidingType(t.roof_type) && !isDeckType(t.roof_type),
  );
  if (onRoof && !hasRoof) {
    return t(
      "This job has no roof measurements yet. Take them on the Roof Measurement step first.",
    );
  }
  if (chosen.some((t) => isSidingType(t.roof_type)) && !hasWalls) {
    return t(
      "This job has no wall measurements yet. Add them on the Wall Measurements card first.",
    );
  }
  if (chosen.some((t) => isDeckType(t.roof_type)) && !hasDeckSize) {
    return t("Type the deck's width and depth first.");
  }
  return null;
}

/**
 * Creating an estimate: from scratch, from the roof measurements, or as a
 * repair somebody types out.
 *
 * From scratch is an empty estimate the rep fills in. From measurements takes a
 * template and a supplier, works the quantities out from the roof that has
 * already been measured, and writes the material order and work order in one
 * step — the arithmetic lives in `generatePlan` in @roof-hawk/core, where it is
 * tested.
 *
 * A custom repair is neither. Jonathon, 2026-08-19: "there needs to be a custom
 * repair option where we can just type in the scope of work since a lot of
 * repairs are custom." No two are alike, there is no template to build and
 * nothing to measure — what there is, is a paragraph describing the work and a
 * price. So this path asks for exactly those, and does the one thing the rep
 * should not have to know about: it lands the estimate on a repair trade, which
 * is what puts the repair contract on the document (ADR 0037) and keeps the
 * automatic permit and manufacturer warranty off a $600 patch.
 *
 * Nothing is invented. A template with no products, or a job with no
 * measurements, produces nothing and says why.
 */
export function CreateEstimateModal({
  job,
  existingCount,
  suppliers,
  trades,
  onClose,
  onCreated,
  onOpenMeasurements,
}: {
  job: Job;
  existingCount: number;
  products: Product[];
  suppliers: Supplier[];
  /** The trades on file, so a repair can be written against the right one. */
  trades: readonly string[];
  onClose: () => void;
  onCreated: (estimateId: string) => Promise<void>;
  /** Leaves for the Roof Measurements card. See `EstimateCard`. */
  onOpenMeasurements?: (() => void) | undefined;
}) {
  const { profileId } = useSession();
  const [path, setPath] = useState<"choose" | "template" | "repair">("choose");
  /*
   * Shingle or metal, asked before the package is.
   *
   * Every roof replacement is sold down one of two paths and the tiers differ
   * (Jonathon, 2026-08-17). Shingle first, because most roofs are. Flat & Other
   * is the third button and is not a way of selling a roof — it is where a
   * template lands when it is neither, so that nothing built is unreachable.
   * See `templatesForPath` in packages/core.
   */
  const [roofPath, setRoofPath] = useState<RoofPath>("Shingle");
  /*
   * The packages to generate, not the package.
   *
   * Jonathon, 2026-08-25: "when selecting shingles vs metal on the estimate
   * builder I want it to ask which ones we'd like to generate and then we
   * select them and it generates them." A customer is shown two or three
   * options and every one of them used to mean coming back to this screen,
   * picking the next tier and waiting again. Tick them once and they are all
   * written — one estimate each, with its own material and work orders.
   */
  const [templateIds, setTemplateIds] = useState<string[]>([]);
  /*
   * A supplier per material, not one for the run.
   *
   * Jonathon, 2026-10-30: "we need to be able to pick two different suppliers
   * because metal is sold one place and shingles etc are sold at another
   * place." A box for each path something is ticked on; a path nobody has
   * touched takes its templates' default supplier. See supplierByPath.ts.
   */
  const [suppliersChosen, setSuppliersChosen] = useState<SuppliersChosen>({});
  /*
   * How this flat roof is built, asked before anything is generated.
   *
   * Only a flat template reads it. Every other template produces the same
   * estimate every time; a TPO roof does not, and the answers decide which
   * lines are ordered rather than only how many. See packages/core/flatRoof.ts.
   */
  const [flatAnswers, setFlatAnswers] = useState<FlatRoofAnswers>(
    defaultFlatRoofAnswers,
  );
  /*
   * How this metal panel roof is finished, asked for the same reason.
   *
   * Painted or galvalume runs through every trim and fastener on the order, and
   * furring strips are three lines and a different screw. See
   * packages/core/src/exposedFastener.ts.
   */
  const [metalAnswers, setMetalAnswers] = useState<ExposedFastenerAnswers>(
    defaultExposedFastenerAnswers,
  );
  /*
   * What a siding job gets, asked for the same reason — and only once the
   * walls are loaded, because the vent count starts at what Hover counted.
   * Null until the rep touches the form; the defaults stand in until then.
   * See packages/core/src/siding.ts.
   */
  const [sidingOverride, setSidingAnswers] = useState<SidingAnswers | null>(
    null,
  );
  /*
   * How big the deck is, how high, and what goes on it. A deck is measured
   * on nothing: these answers are its measurements. See deck.ts.
   */
  const [deckAnswers, setDeckAnswers] =
    useState<DeckAnswers>(defaultDeckAnswers);
  /*
   * The parts of this roof that are not the package.
   *
   * Jonathon, 2026-09-16: "it also needs to ask if any portion of the roof is
   * metal when creating from measurements and which areas are... we need to be
   * able to select whether other parts of the roof have metal or tpo and then
   * proceed accordingly."
   *
   * Empty on almost every job. When it is not, each area is generated from its
   * own measurements with its own template, and those measurements come off
   * the roof the package is generated from — see `roofsWithout`.
   */
  const [sections, setSections] = useState<RoofSection[]>([]);
  /*
   * The customer buys the materials. Jonathon, 2026-09-30, after building job
   * 1084 by hand: a tick on any package, not a template of its own, because
   * the warranty "depends on the package presented". See laborOnly.ts.
   */
  const [laborOnly, setLaborOnly] = useState(false);
  /** Which package is being written, so the button can say so. */
  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The repair form. Its default trade is worked out rather than named, because
  // trades are free text and are never renamed (rule 8).
  const [repairName, setRepairName] = useState("Roof Repair");
  const [repairTrade, setRepairTrade] = useState(
    () => defaultRepairTrade([...trades]) ?? "",
  );
  const [repairScope, setRepairScope] = useState("");
  const [repairPrice, setRepairPrice] = useState("");

  const version = existingCount + 1;

  const templates = useAsync(
    async () =>
      unwrap<RoofTemplate[]>(
        await supabase
          .from("roof_templates")
          .select("*")
          .eq("active", true)
          .order("sort_order")
          .order("name"),
      ),
    [],
  );

  const layouts = useAsync(
    async () =>
      unwrap<RoofLayout[]>(
        await supabase
          .from("job_measurements")
          .select("*")
          .eq("job_id", job.id)
          .order("captured_at"),
      ),
    [job.id],
  );

  /*
   * The walls, for a siding template. Kept apart from the roof: nothing that
   * adds up a job's roof should see a wall, and a siding template is measured
   * off these alone (siding.ts).
   */
  const walls = useAsync(
    async () =>
      unwrap<WallMeasurement[]>(
        await supabase
          .from("job_wall_measurements")
          .select("*")
          .eq("job_id", job.id)
          .order("captured_at"),
      ),
    [job.id],
  );
  const wallRows = walls.data ?? [];
  const sidingAnswers = sidingOverride ?? defaultSidingAnswers(wallRows);

  /** The packages this path offers, in tier order, built ones only. */
  const offered = templatesForPath(templates.data ?? [], roofPath);
  /*
   * Everything ticked, on every path, in the order the paths are offered in.
   *
   * Jonathon, 2026-09-16: "we need to be able to add metal and shingle roof
   * replacement options in the same estimate when we're creating estimate from
   * measurements." A customer shown a shingle roof and a metal roof is being
   * shown two options, and they used to mean two visits to this screen —
   * switching path emptied the ticks, so the first set was thrown away on the
   * way to the second. Now the ticks survive the switch and one run writes
   * both, which is also how the estimate picker reads them: side by side.
   *
   * Deduplicated, because a template can be on two paths: GAF TimberSteel is a
   * metal shingle and is the top of both lists, and ticking it once must not
   * write it twice.
   */
  const chosenTemplates = ROOF_PATHS.flatMap((path) =>
    templatesForPath(templates.data ?? [], path),
  )
    .filter((t) => templateIds.includes(t.id))
    .filter((t, at, all) => all.findIndex((other) => other.id === t.id) === at);
  /** Ticked somewhere other than the list currently on screen. */
  const chosenElsewhere = chosenTemplates.filter(
    (t) => !offered.some((o) => o.id === t.id),
  );
  /** The areas worth generating: measured, and with a package chosen. */
  const liveSections = sections.filter((section) => sectionIsLive(section));
  /** Every template this run will read, the packages and the areas alike. */
  const sectionTemplates = (templates.data ?? []).filter((t) =>
    liveSections.some((section) => section.templateId === t.id),
  );
  const templatesInPlay = [...chosenTemplates, ...sectionTemplates];
  /** The Supplier boxes: one per material being bought, in path order. */
  const pathSuppliers = suppliersByPath(templatesInPlay, suppliersChosen);
  /** Who a template's parts are ordered from on this run. */
  const supplierOf = (template: RoofTemplate | undefined) =>
    template
      ? supplierForTemplate(template, templatesInPlay, suppliersChosen)
      : null;
  /** Whether anything being generated needs the how-is-it-built questions. */
  const anyFlat = templatesInPlay.some((t) => isFlatRoofType(t.roof_type));
  /** Whether anything being generated needs the how-is-it-finished questions. */
  const anyMetalPanel = templatesInPlay.some((t) =>
    isExposedFastenerType(t.roof_type),
  );
  /** Whether anything being generated is siding, and so asks about the house. */
  const anySiding = templatesInPlay.some((t) => isSidingType(t.roof_type));
  /** Whether anything being generated is a deck, and so asks its size. */
  const anyDeck = templatesInPlay.some((t) => isDeckType(t.roof_type));

  const createEstimate = (
    name: string,
    trade: string | null,
    roofType: string | null,
    warranty?: string | null,
    keyDifferentiator?: string | null,
    // Generating three packages at once writes three estimates, and two of them
    // sharing a version number would read as the same option twice.
    forVersion: number = version,
    /**
     * How a generated estimate was generated: the template, the answers and
     * the supplier. Enough to regenerate its parts list later and check a
     * material order against it (ADR 0156). Absent on one built by hand.
     */
    generation?: GenerationRecord & {
      generation_supplier_id: string | null;
      generation_snapshot: GenerationSnapshot | null;
    },
  ) =>
    insertRow<{ id: string }>("estimates", {
      ...generation,
      job_id: job.id,
      name,
      version: forVersion,
      // Not is_active. Which estimate is active is worked out from the three of
      // them — signed, then pinned, then cheapest — so a new option does not
      // take over the job's forecast merely by being newest.
      status: "draft",
      trade,
      roof_type: roofType,
      // Which warranty this roof carries decides an expense worth hundreds, so
      // it comes off the template rather than being assumed.
      warranty: warranty ?? "silver_pledge",
      // The line the customer reads under the package name. The tier above is
      // not it — printing that is what put "golden_pledge" on a proposal.
      key_differentiator: keyDifferentiator ?? null,
      job_overhead_pct: DEFAULT_JOB_OVERHEAD_PCT,
      created_by: profileId,
    });

  async function fromScratch() {
    setBusy(true);
    try {
      const estimate = await createEstimate(`Estimate ${version}`, null, null);
      await onCreated(estimate.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  /**
   * A repair, written out rather than generated.
   *
   * One line item: the heading the customer reads, the scope underneath it, and
   * the price. That is the whole of a repair estimate — everything else on the
   * card (orders, expenses, change orders) is added afterwards if the job turns
   * out to need it.
   *
   * The scope is required. A repair contract's promise is bounded by the scope
   * of work and nothing else — "repairs do not address underlying problems" —
   * so an empty one is a document nobody should sign.
   *
   * `warranty: "none"` because a repair carries none, and the repair contract
   * says so in as many words. Left at the default, a Silver Pledge would be
   * charged as an automatic expense the moment the trade said replacement.
   */
  async function fromCustomRepair() {
    const planned = planRepair({
      asOfDate: todayCentral(),
      job: { id: job.id, jobType: job.job_type },
      version,
      createdByProfileId: profileId,
      heading: repairName,
      trade: repairTrade,
      scope: repairScope,
      ...(repairPrice.trim()
        ? { customerPriceCents: toCents(repairPrice) }
        : {}),
      references: {
        validRepairTrades: trades.filter(isRepairTrade),
        products: [],
        suppliers: [],
        productPrices: [],
        crews: [],
      },
    });
    if (!planned.ok) {
      const first = planned.errors[0];
      setError(
        first?.code === "missing_scope"
          ? t(
              "Type what the repair covers. It is what the customer signs for, and the repair contract promises nothing beyond it.",
            )
          : first?.message
            ? t(first.message)
            : t("The repair could not be planned."),
      );
      return;
    }

    setBusy(true);
    try {
      const { ref: _estimateRef, ...estimateRow } = planned.plan.estimate;
      const estimate = await insertRow<{ id: string }>(
        "estimates",
        estimateRow,
      );
      const {
        ref: _lineRef,
        estimateRef: _parentRef,
        product_id: _scopeProductId,
        customerPriceCents,
        discountCents,
        ...scopeLine
      } = planned.plan.scopeLine;
      await insertRow("estimate_items", {
        estimate_id: estimate.id,
        ...scopeLine,
        customer_price: toDecimal(customerPriceCents),
        discount: toDecimal(discountCents),
      });
      await onCreated(estimate.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  /**
   * One part of an estimate: a template, generated against one set of roofs.
   *
   * A replacement is usually one part — the package, on the whole roof. A roof
   * with a metal porch on it is two: the shingle package on what is left of
   * the house, and the panel package on the porch. Jonathon, 2026-09-16:
   * "those measurements will be subtracted from the rest of the measurements
   * so that we don't double charge."
   *
   * Everything a part writes belongs to the estimate the caller has already
   * made: its material orders (one per supplier), its work order, and the one
   * line the customer reads. What it does not write is a price. The margin is
   * solved once for the whole estimate — a porch does not carry its own
   * overhead, its own permit or its own general liability — and the answer is
   * split between the parts afterwards. See `priceParts`.
   *
   * Returns what could not be worked out and what has to be said before the
   * order goes out, rather than driving the screen itself: the caller may be
   * generating several packages and one report at the end beats one dialog
   * per package. It has to be a report and not something drawn in this modal,
   * because the caller closes the modal the moment the estimates exist.
   */
  async function planPart(
    templateId: string,
    roofs: readonly PartRoof[],
    supplier: string | null,
  ) {
    const template = (templates.data ?? []).find((t) => t.id === templateId);
    const [itemRows, productRows, priceRows, supplierRows] = await Promise.all([
      supabase
        .from("roof_template_items")
        .select(
          "id,slot,product_id,qty,default_qty,supplier_id,coverage,section," +
            "applies_when,girth_source,count_source,sort_order",
        )
        .eq("template_id", templateId)
        .order("sort_order"),
      supabase
        .from("products")
        .select(
          // finish and galvalume_twin_id are read by `finishIndex` for the
          // painted-to-galvalume swap on a metal panel roof.
          "id,name,description,customer_wording,category,kind,unit,default_cost,coverage,waste,measurement,color_rule,color_options,finish,galvalume_twin_id",
        )
        .eq("active", true),
      // Prices belong to the supplier, not the catalogue: the same ridge
      // cap is $23.01 from Central States and $26.50 from Heely-Brown.
      supabase.from("product_prices").select("price_list_id,product_id,cost"),
      supabase.from("suppliers").select("id,price_list_id"),
    ]);

    const allItems = unwrap<RoofTemplateItem[]>(itemRows);
    if (allItems.length === 0) {
      throw new Error(
        t(
          '"{template}" has no products on it yet — add them under Tools → Templates.',
          { template: String(template?.name) },
        ),
      );
    }

    const catalogue = unwrap<GeneratorProduct[] & ColorProduct[]>(productRows);

    /*
     * Which lines, in which parts, for the answers given.
     *
     * On a flat roof the answers decide which lines exist at all. On a metal
     * panel roof the furring answer does, and the finish decides which part
     * each line is — swapped after selection and before pricing, so the
     * galvalume part's own cost, coverage and measurement are what the
     * generator reads. Every other template comes through untouched.
     *
     * The rule is `linesToPlan` in packages/core, shared with the orders'
     * From Measurements screen so the two cannot disagree (CLAUDE.md rule 5).
     */
    const answers: TemplateAnswers = {
      flat: flatAnswers,
      metal: metalAnswers,
      siding: sidingAnswers,
      deck: deckAnswers,
      laborOnly,
    };
    const lines = linesToPlan(template, allItems, catalogue, roofs, answers);
    // Written onto every order this part produces, and onto the estimate for
    // its main part, so the order can be checked against the estimate after
    // somebody edits it (ADR 0156). A part in another material carries its own
    // template, and is frozen as one of the estimate's areas, so its orders are
    // checked against it (ADR 0166).
    const generation = generationRecord(templateId, answers);
    if (lines.items.length === 0) {
      throw new Error(
        t('Nothing on "{template}" applies to the build you described.', {
          template: String(template?.name),
        }),
      );
    }

    const priceList = unwrap<ProductPrice[]>(priceRows);
    const supplierList = unwrap<PricedSupplier[]>(supplierRows);
    const prices = new PriceBook(priceList, supplierList);
    // Everything the parts list was built from, frozen on the estimate, so a
    // later template, catalogue or price edit is not read as a difference on
    // its orders (see generationSnapshot.ts). The roofs are this part's own —
    // for the main package, the roof less the areas in another material.
    const snapshot = template
      ? generationSnapshot({
          template,
          templateItems: allItems,
          catalogue,
          prices: priceList,
          suppliers: supplierList,
          roofs,
        })
      : null;
    const plan = generatePlan(
      lines.items,
      // A flat roof's insulation answer rides along on the roof, because it
      // decides which area the insulation is bought against — the roof
      // alone, or the roof and the parapets. See `resolveMeasurement`.
      lines.roofs,
      catalogue,
      prices,
      supplier,
      // A shingle material order is checked against the roof it is going on,
      // so it reads in the order the roof is built. Metal keeps its own
      // sequence, which is set per template line.
      template?.roof_type === "Shingle" ? { installOrder: "shingle" } : {},
    );

    // The framing nails the template names, bought in the kind the crew's gun
    // takes. The crew is the template's: it is the one `writePart` puts on the
    // work order (ADR 0287).
    const nails = await fetchFramingNails({
      trade: template?.trade ?? null,
      fallbackCrewId: template?.default_crew_id ?? null,
    });
    plan.materialItems = fitFramingNails(
      plan.materialItems,
      nails.nailer,
      nails.tag<GeneratorProduct>(catalogue),
      (product, line) => prices.costOf(product, line.supplierId ?? supplier),
    );

    return {
      template,
      roofs,
      catalogue,
      lines,
      plan,
      supplier,
      generation,
      snapshot,
    };
  }
  type PlannedPart = Awaited<ReturnType<typeof planPart>>;

  /**
   * Write one planned part onto an estimate that already exists.
   *
   * Split from `planPart` so that everything that can refuse — a template
   * with no products, a build nothing on the template applies to — has
   * refused before the estimate is created. Planning a porch after the
   * house's orders were written left an unpriced estimate behind whenever the
   * porch refused.
   */
  async function writePart(
    estimate: { id: string },
    planned: PlannedPart,
    part: { label: string | null; sortOrder: number },
  ): Promise<WrittenPart> {
    const { template, roofs, catalogue, lines, plan, supplier, generation } =
      planned;

    /*
     * The package's own wording, as customer-visible lines on the estimate.
     *
     * These are what the proposal draws as Scope of Work, Warranty and Worry
     * Free Wood Bonus — the words the customer picks a package on. They are
     * lines rather than a field so they can be edited per job, and they
     * carry no price: the package's price is the headline line below. The
     * builder does not show them (Jonathon, 2026-08-24: an option is one
     * line item there) — they are edited on the Present screen, where the
     * customer reads them. ADR 0061.
     *
     * Only the package's own. A metal porch inside a shingle estimate is a
     * line on that estimate, not a second Scope of Work block: the proposal
     * draws one of each, and a second set prints the same headings twice with
     * different words under them. What the porch is getting is said on its own
     * line instead, which is where the customer reads it.
     */
    if (part.label === null) {
      for (const copy of plan.copyItems) {
        await insertRow("estimate_items", {
          estimate_id: estimate.id,
          product_id: copy.product_id,
          trade: template?.trade ?? null,
          description: copy.description,
          note: copy.note,
          section: copy.section,
          qty: 1,
          customer_price: 0,
          discount: 0,
          work_doing: true,
          customer_visible: true,
          sort_order: copy.sort_order,
        });
      }
    }

    /*
     * The scope of work a flat roof actually got.
     *
     * Jonathon, 2026-08-31: "whatever's selected in the estimate builder
     * needs to reflect appropriately in the scope of work as well." Written
     * from the answers rather than taken off the template, because the
     * template describes every build it can produce and this estimate is one
     * of them — a stored paragraph would go on promising a tear-off on a new
     * build.
     *
     * A customer-visible line with no price, like the package wording above,
     * so it is edited on the Present screen alongside it.
     *
     * A flat roof's scope is a line of its own, because a flat template has
     * no scope text to start from — the answers are the whole of it.
     *
     * A metal panel roof is the other way round: its template already carries
     * a scope, and that lands as the note on the headline line below. A
     * second line here would print the scope twice on the same proposal, so
     * the two answers are appended to the template's scope instead. See
     * `metalScopeNote`.
     *
     * Labor only opens the scope the customer reads (ADR 0240). On a flat
     * roof's main part that is this line, which prints first, so the sentence
     * goes here and stays off the headline line below, where it would print
     * after the bullets and twice.
     */
    const flatScope =
      lines.questions === "flat"
        ? flatRoofScopeOfWork(flatAnswers)
            .map((line) => `• ${line}`)
            .join("\n")
        : null;
    if (flatScope && part.label === null) {
      await insertRow("estimate_items", {
        estimate_id: estimate.id,
        trade: template?.trade ?? null,
        description: "Scope of Work",
        note: laborOnly ? laborOnlyScope(flatScope) : flatScope,
        section: "Scope of Work",
        qty: 1,
        customer_price: 0,
        discount: 0,
        work_doing: true,
        customer_visible: true,
        sort_order: -10,
      });
    }

    // The siding itself, when this is a siding part: those lines take the
    // panel colour and the rest of the coloured lines the trim colour
    // (siding.ts). Read here, before `lines` is shadowed by each supplier's
    // lines below.
    const sidingPanels =
      lines.questions === "siding" ? sidingPanelProductIds(lines.items) : null;

    // One order per supplier. Most templates buy everything from one place
    // and produce one order; an AG panel roof buys its furring strips and
    // nails from Home Depot while the panels come from Central States.
    const bySupplier = new Map<string, typeof plan.materialItems>();
    for (const line of plan.materialItems) {
      const key = line.supplierId ?? supplier ?? "";
      bySupplier.set(key, [...(bySupplier.get(key) ?? []), line]);
    }

    for (const [supplier, lines] of bySupplier) {
      const order = await insertRow<{ id: string }>("material_orders", {
        ...generation,
        job_id: job.id,
        estimate_id: estimate.id,
        supplier_id: supplier || null,
        trade: template?.trade ?? null,
        status: "not_placed",
        tax_rate: DEFAULT_MATERIAL_TAX_RATE,
        created_by: profileId,
      });
      for (const line of lines) {
        await insertRow("material_order_items", {
          material_order_id: order.id,
          product_id: line.product_id,
          name: line.description,
          qty: line.qty,
          unit: line.unit,
          unit_price: toDecimal(line.unitPrice),
          calc_note: line.calc,
          // Colour-matched products are filled in rather than asked about;
          // everything else stays blank and blocks the order until settled.
          // A siding order takes the panel and trim colours the rep chose
          // (siding.ts); a line nothing was chosen for stays blank the same.
          color:
            (sidingPanels
              ? sidingLineColor(
                  catalogue.find((p) => p.id === line.product_id),
                  sidingPanels,
                  sidingAnswers,
                )
              : null) ??
            defaultColorFor(catalogue.find((p) => p.id === line.product_id)),
          sort_order: line.sort_order,
        });
      }
    }

    if (plan.workItems.length > 0) {
      // The template says whose work this is — shingle goes to Pablo,
      // standing seam to Scott, set on the template itself.
      //
      // A template that names nobody leaves the crew blank rather than
      // falling back to Scott, which is what this did. The crew is not a
      // label: one we carry workers comp for is charged at 13.17% of the work
      // order, so the old fallback quietly put Scott's comp on every shingle
      // roof the company estimated. A blank crew is visible and costs
      // nothing; a wrong one is neither.
      const order = await insertRow<{ id: string }>("work_orders", {
        job_id: job.id,
        estimate_id: estimate.id,
        crew_id: template?.default_crew_id ?? null,
        trades: template?.trade ? [template.trade] : null,
        status: "not_scheduled",
        tax_rate: DEFAULT_WORK_TAX_RATE,
        created_by: profileId,
      });
      for (const line of plan.workItems) {
        await insertRow("work_order_items", {
          work_order_id: order.id,
          product_id: line.product_id,
          description: line.description,
          qty: line.qty,
          unit: line.unit,
          unit_price: toDecimal(line.unitPrice),
          calc_note: line.calc,
          sort_order: line.sort_order,
        });
      }
    }

    // The customer's side of this part: one line, named after what is being
    // laid and measured in the squares that are actually being covered. The
    // price lands on it once the whole estimate is priced.
    const product = catalogue.find((p) => p.id === lines.headlineProductId);
    const named = product
      ? estimateLineDescription(product)
      : (template?.name ?? null);
    // A deck is sold by the square foot it covers, not by the square.
    const onDeck = lines.questions === "deck";
    const squares = onDeck
      ? deckSquareFeet(deckAnswers)
      : headlineSquares(roofs);
    const qty = squares > 0 ? Number(squares.toFixed(2)) : 1;
    /*
     * A metal panel roof's scope is assembled from the finish-neutral
     * sections, the answers, and the warranty for the finish chosen — so a
     * galvalume roof never carries a paint warranty. Undefined everywhere
     * else, which writes the template's scope as it stands.
     */
    const scope =
      lines.scopeNote !== undefined && !(laborOnly && flatScope && !part.label)
        ? lines.scopeNote
        : (template?.scope ?? null);
    const line = await insertRow<{ id: string }>("estimate_items", {
      estimate_id: estimate.id,
      product_id: lines.headlineProductId,
      trade: template?.trade ?? null,
      description: part.label ? `${part.label} — ${named ?? "Roof"}` : named,
      qty,
      unit: onDeck ? "SF" : "SQ",
      customer_price: 0,
      discount: 0,
      work_doing: true,
      // A part's own scope is on its own line, flat answers and all: it is the
      // only place the customer reads what that piece of roof is getting.
      note:
        part.label && flatScope
          ? [scope, flatScope].filter(Boolean).join("\n\n")
          : scope,
      sort_order: part.sortOrder,
    });

    const label = part.label
      ? `${part.label} (${template?.package_name?.trim() || template?.name || t("Package")})`
      : template?.package_name?.trim() || template?.name || t("Package");

    return {
      lineId: line.id,
      qty,
      // What this part costs us, which is how the contract is divided between
      // the parts. Off the plan rather than the database: the total comes from
      // the ladder either way, and this only has to say which share is whose.
      cost: [...plan.materialItems, ...plan.workItems].reduce(
        (total, item) => total + item.qty * item.unitPrice,
        0,
      ),
      label,
      skipped: plan.skipped.map((skip) => ({
        name: `${label} — ${skip.name}`,
        reason: skip.reason,
      })),
      // Only a metal panel package has any, and each names its package: the
      // rep may have ticked three, and the office corrects one order.
      warnings: lines.warnings.map((warning) => `${label}: ${warning}`),
    };
  }

  /**
   * Price the estimate once, then divide it between the parts.
   *
   * The price is worked out from the orders that were actually written, read
   * back from the database — not from a forecast of them. An earlier version
   * predicted the costs from the plan and was wrong whenever reality differed
   * by so much as a tax rate: a supplier on a different rate put the job out by
   * three points, which is the kind of error nobody catches by eye.
   *
   * `contractBaseForMargin` solves for the contract that produces the margin
   * rather than marking cost up, because general liability, the permit and the
   * warranty are themselves slices of revenue. The rate per square falls out of
   * the total, which is the direction a roof is actually sold in.
   *
   * Where an estimate has more than one part, the answer is split between them
   * in proportion to what each one costs, and the rounding goes on the first.
   * Solving each part on its own would be wrong twice over: the percentage
   * costs would be counted once per part, and the parts would not add up to the
   * estimate the ladder shows.
   *
   * This is a starting point: the gross profit percentage on the estimate is
   * clickable, and any line can be edited by hand.
   */
  async function priceParts(
    estimate: { id: string },
    template: RoofTemplate | undefined,
    parts: readonly WrittenPart[],
  ) {
    if (parts.length === 0) return;

    // Everything is on the job now, so ask what it really costs.
    const children = await fetchEstimateChildren(estimate.id);
    const layouts = await fetchRoofLayouts(job.id);

    const base = contractBaseForMargin(
      {
        estimate: {
          office_discount: 0,
          job_overhead_pct: DEFAULT_JOB_OVERHEAD_PCT,
          trade: template?.trade ?? null,
          roof_type: template?.roof_type ?? null,
          warranty: template?.warranty,
        },
        jobType: job.job_type,
        // No date yet: it is being made now, so a Mountain Brook job is
        // priced at the city's permit fee (ADR 0289).
        place: job,
        layouts,
        workOrderCrews: children.workOrders.map((o) => o.crew ?? null),
        items: [],
        changeOrders: children.changeOrders,
        materialOrders: children.materialOrders,
        workOrders: children.workOrders,
        expenses: children.expenses,
      },
      TARGET_MARGIN,
    );

    if (base <= 0) return;

    // Divided in proportion to what each part costs us, and adding up to the
    // contract the ladder solved for, to the cent. The rule is `splitCents` in
    // packages/core (CLAUDE.md rules 3 and 5).
    const shares = splitCents(
      base,
      parts.map((part) => part.cost),
    );

    for (const [at, part] of parts.entries()) {
      const amount = shares[at] ?? 0;
      await updateRow("estimate_items", part.lineId, {
        unit_price: rateFromTotal(amount as Cents, part.qty),
        customer_price: toDecimal(amount as Cents),
      });
    }
  }

  /**
   * Write one package: its estimate, its parts, and the price across them.
   *
   * The package is generated against what is left of the roof once the areas
   * in another material have been taken out of it, and each of those areas is
   * generated against its own measurements with its own template. One
   * estimate, one price, and a material order per supplier per part — because
   * the porch's panels and the house's shingles are two deliveries.
   */
  async function generateOne(
    templateId: string,
    roofs: readonly RoofLayout[],
    parts: readonly RoofSection[],
    version: number,
  ): Promise<{
    id: string;
    skipped: { name: string; reason: string }[];
    warnings: string[];
  }> {
    const template = (templates.data ?? []).find((t) => t.id === templateId);
    /*
     * A siding package is measured on the walls, not the roof, and a part of
     * the roof in another material has nothing to do with it — so it is
     * generated against the walls alone and the sections are left to the
     * roof packages.
     */
    const siding = isSidingType(template?.roof_type);
    // Nor is a deck, which is measured on nothing but its own answers.
    const deck = isDeckType(template?.roof_type);
    const sections = siding || deck ? [] : parts;

    // Every part is planned before the estimate exists, so a part that
    // refuses stops the package with nothing written. See `writePart`.
    const planned: {
      plan: PlannedPart;
      label: string | null;
      sortOrder: number;
    }[] = [
      {
        // The main package, on the roof less the areas in another material.
        plan: await planPart(
          templateId,
          deck ? [] : siding ? wallRows : roofsWithout(roofs, sections),
          supplierOf(template),
        ),
        label: null,
        sortOrder: 10,
      },
    ];
    for (const [at, section] of sections.entries()) {
      setGenerating(section.name.trim() || t("the rest of the roof"));
      // An area is bought from its own material's supplier: the metal
      // porch's panels come from the Metal supplier, not from the shingle
      // supplier the house is ordered from.
      const own = (templates.data ?? []).find(
        (t) => t.id === section.templateId,
      );
      planned.push({
        plan: await planPart(
          section.templateId,
          [sectionRoof(section, roofs)],
          supplierOf(own),
        ),
        label: section.name.trim() || "Other area",
        sortOrder: 20 + at * 10,
      });
    }

    // The estimate remembers its main part's recipe (ADR 0156): that is the
    // package it was generated as. The parts in another material keep theirs
    // on their own orders, and are frozen beside the main part's copy as its
    // areas, so those orders are checked too (ADR 0166).
    const main = planned[0]!.plan;
    const areas = planned.slice(1).flatMap(({ plan, label }) =>
      plan.snapshot
        ? [
            {
              label: label ?? "Other area",
              supplierId: plan.supplier,
              snapshot: plan.snapshot,
            },
          ]
        : [],
    );
    const named = template?.name ?? `Estimate ${version}`;
    const estimate = await createEstimate(
      laborOnly ? laborOnlyName(named) : named,
      template?.trade ?? null,
      template?.roof_type ?? null,
      template?.warranty,
      template?.key_differentiator,
      version,
      {
        ...main.generation,
        generation_supplier_id: main.supplier,
        generation_snapshot: main.snapshot
          ? withAreas(main.snapshot, areas)
          : null,
      },
    );

    const written: WrittenPart[] = [];
    for (const part of planned) {
      written.push(
        await writePart(estimate, part.plan, {
          label: part.label,
          sortOrder: part.sortOrder,
        }),
      );
    }

    await priceParts(estimate, template, written);

    return {
      id: estimate.id,
      skipped: written.flatMap((part) => part.skipped),
      warnings: written.flatMap((part) => part.warnings),
    };
  }

  /**
   * Every package that was ticked, one after the other.
   *
   * In tier order, because that is the order they were offered in and the order
   * the customer will read them. The job lands on the first one; the rest are
   * there in the estimate picker. Packages ticked on more than one path are
   * generated together — a metal option and a shingle option are two estimates
   * written in one go, which is what the customer is shown side by side.
   *
   * One failure stops the run. A half-written package is worse than none — its
   * orders exist and its headline price does not — so what has been created
   * stays, the error names the package it stopped on, and the rest are not
   * attempted.
   */
  async function fromMeasurements() {
    // Every roof on the job, not the first one. A house with a detached garage
    // is two layouts, and ordering for one of them ships a short load.
    const roofs = layouts.data ?? [];
    if (chosenTemplates.length === 0) {
      setError(t("Tick the packages to generate."));
      return;
    }
    const unmeasured = missingMeasurements(
      chosenTemplates,
      roofs.length > 0,
      wallRows.length > 0,
      deckSquareFeet(deckAnswers) > 0,
    );
    if (unmeasured) {
      setError(unmeasured);
      return;
    }

    setBusy(true);
    const skipped: { name: string; reason: string }[] = [];
    const warnings: string[] = [...sectionWarnings(roofs, sections)];
    const created: string[] = [];

    /*
     * One report for the whole run, shown before the caller closes this screen.
     *
     * It has to be shown here, and it has to be a dialog: `onCreated` closes
     * the modal, so anything drawn in it is gone before anybody reads it. That
     * is how the painted-part warning went unseen — it was rendered into this
     * modal on the way out of it.
     */
    const report = (): string | null => {
      const parts: string[] = [];
      if (warnings.length > 0) {
        parts.push(
          t("Check before the material order is sent:") +
            "\n\n" +
            warnings.map((w) => `• ${w}`).join("\n"),
        );
      }
      if (skipped.length > 0) {
        parts.push(
          t("{n} line(s) could not be worked out:", { n: skipped.length }) +
            "\n\n" +
            skipped.map((s) => `• ${s.name} — ${s.reason}`).join("\n"),
        );
      }
      return parts.length > 0
        ? `${t("Created {n} estimate(s).", { n: created.length })} ${parts.join("\n\n")}`
        : null;
    };

    try {
      for (const [at, template] of chosenTemplates.entries()) {
        setGenerating(template.package_name?.trim() || template.name);
        const result = await generateOne(
          template.id,
          roofs,
          liveSections,
          version + at,
        );
        created.push(result.id);
        skipped.push(...result.skipped);
        warnings.push(...result.warnings);
      }
    } catch (e) {
      setGenerating(null);
      const message = `${e instanceof Error ? e.message : String(e)}${
        created.length
          ? ` ${t("({n} package(s) were created before this.)", { n: created.length })}`
          : ""
      }`;
      setError(message);
      setBusy(false);
      if (created[0]) {
        // The same reason as above: the error is drawn into a modal that is
        // about to close, so it is said out loud first.
        window.alert([message, report()].filter(Boolean).join("\n\n"));
        await onCreated(created[0]);
      }
      return;
    }

    setGenerating(null);
    const said = report();
    if (said) window.alert(said);

    await onCreated(created[0]!);
  }

  if (path === "choose") {
    return (
      <Modal
        title={t("Create Estimate")}
        subtitle={t(
          "Start from nothing, or build it from the roof measurements.",
        )}
        onClose={onClose}
        actions={
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
        }
      >
        {error && <div className="err">{error}</div>}
        <div className="choosegrid">
          <button
            className="choosecard"
            disabled={busy}
            onClick={() => void fromScratch()}
          >
            <b>{t("Create From Scratch")}</b>
            <span>{t("An empty estimate. Add the line items yourself.")}</span>
          </button>
          <button
            className="choosecard"
            disabled={busy}
            onClick={() => {
              setError(null);
              setPath("template");
            }}
          >
            <b>{t("Create From Measurements")}</b>
            <span>
              {t(
                "Pick a template and the quantities come off the roof, with the material and work orders written for you.",
              )}
            </span>
          </button>
          <button
            className="choosecard"
            disabled={busy}
            onClick={() => {
              setError(null);
              setPath("repair");
            }}
          >
            <b>{t("Custom Repair")}</b>
            <span>
              {t(
                "Type the scope of work and the price. No template, no measurements — the repair contract goes on it.",
              )}
            </span>
          </button>
        </div>
      </Modal>
    );
  }

  if (path === "repair") {
    return (
      <Modal
        title={t("Custom Repair")}
        subtitle={t(
          "What the customer reads, in your words. Everything else can be added afterwards.",
        )}
        onClose={onClose}
        actions={
          <>
            <button className="btn-ghost" onClick={() => setPath("choose")}>
              {t("Cancel")}
            </button>
            <button
              className="btn-primary"
              disabled={busy}
              onClick={() => void fromCustomRepair()}
            >
              {busy ? t("Creating…") : t("Create Estimate")}
            </button>
          </>
        }
      >
        {error && <div className="err">{error}</div>}

        <div className="fgrid one">
          <div className="field">
            <label>{t("Name")}</label>
            <input
              placeholder={t("What the customer sees as the heading")}
              value={repairName}
              onChange={(e) => setRepairName(e.target.value)}
            />
          </div>

          <div className="field">
            <label>{t("Trade")}</label>
            <select
              value={repairTrade}
              onChange={(e) => setRepairTrade(e.target.value)}
            >
              <option value="">—</option>
              {trades.filter(isRepairTrade).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <span className="na-hint">
              {t(
                "A repair trade is what puts the repair contract on the document, and keeps the permit and the manufacturer warranty off the price.",
              )}
            </span>
          </div>

          <div className="field">
            <label>
              {t("Scope of Work")} <span className="req">*</span>
            </label>
            <textarea
              rows={12}
              placeholder={t(
                "What is wrong, what we will do about it, what is included and what is not.",
              )}
              value={repairScope}
              onChange={(e) => setRepairScope(e.target.value)}
            />
          </div>

          <div className="field">
            <label>{t("Price")}</label>
            <input
              inputMode="decimal"
              placeholder="0.00"
              value={repairPrice}
              onChange={(e) => setRepairPrice(e.target.value)}
            />
            <span className="na-hint">
              {t(
                "What the customer pays. Change it, or add more lines, on the estimate afterwards.",
              )}
            </span>
          </div>
        </div>
      </Modal>
    );
  }

  const roofs = layouts.data ?? [];

  /**
   * Nothing to measure from.
   *
   * This is a dead end, and it used to be discovered at the very end: the rep
   * chose a package, chose a supplier, pressed Submit, and only then was told
   * to go and take measurements — with no way to get there. Now the screen
   * says so before any of that, and offers the step.
   *
   * On the Siding path it is the walls that have to be measured, not the roof.
   */
  const onWalls = roofPath === "Siding";
  // A deck is never unmeasured: its size is asked for below.
  const onDeck = roofPath === "Decks";
  const unmeasured = onDeck
    ? false
    : onWalls
      ? wallRows.length === 0
      : roofs.length === 0;

  return (
    <Modal
      title={t("Generate from the roof measurements")}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={() => setPath("choose")}>
            {t("Back")}
          </button>
          {unmeasured ? (
            onOpenMeasurements && (
              <button className="btn-primary" onClick={onOpenMeasurements}>
                {t("Take measurements")}
              </button>
            )
          ) : (
            <button
              className="btn-primary"
              disabled={busy || chosenTemplates.length === 0}
              title={
                !busy && chosenTemplates.length === 0
                  ? t("Tick at least one package first")
                  : undefined
              }
              style={
                busy || chosenTemplates.length === 0
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : undefined
              }
              aria-busy={busy}
              onClick={() => void fromMeasurements()}
            >
              {busy && <Spinner />}
              {busy
                ? generating
                  ? t("Generating {name}…", { name: generating })
                  : t("Generating…")
                : chosenTemplates.length > 1
                  ? t("Generate {n} Estimates", { n: chosenTemplates.length })
                  : t("Generate Estimate")}
            </button>
          )}
        </>
      }
    >
      {error && <div className="err">{error}</div>}

      {/* The path is offered whether or not anything is measured: on a job
          with no roof the rep still has to be able to reach Siding, which is
          measured on the walls. Only the list under it waits. */}
      <div className="fgrid one">
        <div className="field">
          <label>
            {t("Packages")} <span className="req">*</span>
          </label>
          {/* Colour-coded, and the colour carries down to the list below it.
              Until 2026-08-24 `.seg` and `.on` had no styles at all, so all
              three buttons looked identical whichever was chosen and nothing
              on the screen said which list you were reading. */}
          <div
            className="seg"
            role="group"
            aria-label={t("Shingle, metal or flat")}
          >
            {ROOF_PATHS.map((option) => (
              <button
                key={option}
                type="button"
                className={`${PATH_CLASS[option]}${roofPath === option ? " on" : ""}`}
                aria-pressed={roofPath === option}
                // The ticks are kept. Shingle and metal are two options on
                // the same job as often as they are two different jobs, and
                // emptying the list on the way between them is what made
                // offering both a second trip through this screen.
                onClick={() => setRoofPath(option)}
              >
                {t(option)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {unmeasured && (
        <div className="config-warn" role="status">
          <strong>
            {onWalls
              ? t("This job has no wall measurements yet.")
              : t("This job has no roof measurements yet.")}
          </strong>{" "}
          {onWalls
            ? t(
                "A siding estimate needs the walls measured — from the Hover report, or keyed in — to work the quantities out from.",
              )
            : t(
                "An estimate generated from measurements needs a measured roof to work the quantities out from.",
              )}{" "}
          {onOpenMeasurements
            ? onWalls
              ? t(
                  "Take them on the Wall Measurements card, then come back here.",
                )
              : t(
                  "Take them on the Roof Measurements card, then come back here.",
                )
            : onWalls
              ? t("Take them on the Wall Measurements card first.")
              : t("Take them on the Roof Measurements card first.")}
        </div>
      )}

      {/* Hidden rather than disabled when there is nothing to measure from:
          choosing a package for an estimate that cannot be generated is work
          thrown away, and it was the work the old screen invited first. */}
      {!unmeasured && (
        <div className="fgrid one">
          <div className="field">
            {/* In tier order, and only the ones that are actually built: a
                template with no products on it generates an estimate with
                three lines and a margin that means nothing, so it is offered
                in Tools, where it is built, and nowhere else (Jonathon,
                2026-08-17).

                Tick as many as the customer is being shown. Each becomes its
                own estimate, with its own material and work orders. */}
            <div className={`pathlist ${PATH_CLASS[roofPath]}`}>
              <span className="pathname">
                {t("{path} packages", { path: t(roofPath) })}
              </span>
              <div className="picklist" role="group" aria-label={t("Packages")}>
                {offered.length === 0 ? (
                  <div className="na-hint">
                    {t(
                      "No {material} package is built out yet. Build one under Tools → Templates.",
                      { material: t(roofPath).toLowerCase() },
                    )}
                  </div>
                ) : (
                  offered.map((t) => (
                    <label className="pickrow" key={t.id}>
                      <input
                        type="checkbox"
                        checked={templateIds.includes(t.id)}
                        onChange={(e) => {
                          setTemplateIds((was) =>
                            e.target.checked
                              ? [...was, t.id]
                              : was.filter((id) => id !== t.id),
                          );
                          // A template that always goes to one supplier fills
                          // its material's Supplier box until somebody
                          // chooses (supplierByPath.ts).
                        }}
                      />
                      <span>
                        {t.package_name?.trim()
                          ? `${t.package_name} — ${t.name}`
                          : t.name}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            {chosenElsewhere.length > 0 && (
              // What is ticked on the lists that are not on screen. Without
              // it, switching to Metal to add an option reads as though the
              // shingle one had been dropped.
              <div className="na-hint" style={{ marginTop: 8 }}>
                {t("Also generating: {names}.", {
                  names: chosenElsewhere
                    .map((each) => each.package_name?.trim() || each.name)
                    .join(", "),
                })}
              </div>
            )}
            {chosenTemplates.some(templateIsUnbuilt) && (
              // Said twice on purpose. The suffix in the list is easy to skim
              // past, and by this point they have chosen it.
              <div
                className="config-warn"
                role="status"
                style={{ margin: "8px 0 0" }}
              >
                <strong>{t(UNBUILT_TEMPLATE_LABEL)}.</strong>{" "}
                {t(UNBUILT_TEMPLATE_EXPLANATION)}
              </div>
            )}
            {/* A flat roof is asked how it is built before it is generated.
                Only here, and only when a flat package is ticked: every other
                template produces the same estimate every time. */}
            {anyFlat && (
              <FlatRoofQuestions
                answers={flatAnswers}
                onChange={setFlatAnswers}
                roofs={roofs}
              />
            )}
            {/* And a metal panel roof is asked how it is finished. Same place,
                same shape, same reason: the answers change what is bought. */}
            {anyMetalPanel && (
              <MetalPanelQuestions
                answers={metalAnswers}
                onChange={setMetalAnswers}
              />
            )}
            {/* And a siding job is asked what the house gets, what colour,
                and how many of each vent and block. */}
            {anySiding && (
              <SidingQuestions
                answers={sidingAnswers}
                onChange={setSidingAnswers}
                walls={wallRows}
              />
            )}
            {/* And a deck is asked its size, height, steps and railing. */}
            {anyDeck && (
              <DeckQuestions answers={deckAnswers} onChange={setDeckAnswers} />
            )}
            <label className="pickrow labor-only">
              <input
                type="checkbox"
                checked={laborOnly}
                onChange={(e) => setLaborOnly(e.target.checked)}
              />
              <span>
                <b>{t("Labor only")}</b>
                <span className="na-hint">
                  {" "}
                  {t(
                    'The customer buys the materials. No material order is written, and the scope starts "{heading}". The package\'s warranty and wording stay as they are.',
                    { heading: LABOR_ONLY_SCOPE_HEADING },
                  )}
                </span>
              </span>
            </label>
          </div>
          {/* And whether any of this roof is something else entirely. A porch
              in panel, a dormer in TPO: measured on its own, generated on its
              own, and taken off the roof the package above is priced from.
              Not on the Siding path — a wall has no parts in another roof. */}
          {!onWalls && !onDeck && (
            <RoofSectionsField
              sections={sections}
              onChange={setSections}
              templates={templates.data ?? []}
              roofs={roofs}
            />
          )}
          {/* One Supplier box per material being bought: shingles from one
              place, metal from another. With nothing ticked yet it is the
              box for the path on screen, so the form does not jump. */}
          {(pathSuppliers.length > 0
            ? pathSuppliers
            : [{ path: roofPath, supplierId: suppliersChosen[roofPath] ?? "" }]
          ).map(({ path: material, supplierId }) => (
            <div className="field" key={material}>
              <label>
                {pathSuppliers.length > 1
                  ? t("{material} supplier", { material })
                  : t("Supplier")}
              </label>
              <select
                aria-label={`${material} supplier`}
                value={supplierId}
                onChange={(e) =>
                  setSuppliersChosen((was) => ({
                    ...was,
                    [material]: e.target.value,
                  }))
                }
              >
                <option value="">{t("Supplier")}</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Only when there is something to say. The empty case is the banner at
          the top of the screen now, not a grey line under a form. */}
      {!unmeasured && !onWalls && !onDeck && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "Measuring from {layouts} — {squares} squares in total, {waste}% waste.",
            {
              layouts: roofs
                .map((r) => `"${r.name ?? t("the roof layout")}"`)
                .join(" + "),
              squares: roofs.reduce(
                (total, r) => total + Number(r.squares ?? 0),
                0,
              ),
              waste: Math.max(0, ...roofs.map((r) => Number(r.waste_pct ?? 0))),
            },
          )}
        </div>
      )}
      {!unmeasured && onWalls && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "Measuring from {walls} — {squares} squares of siding and trim, {waste}% waste.",
            {
              walls: wallRows
                .map((w) => `"${w.name ?? t("the walls")}"`)
                .join(" + "),
              squares: wallRows
                .reduce((total, w) => total + sidingSquaresOf(w), 0)
                .toFixed(2),
              waste: Math.max(
                0,
                ...wallRows.map((w) => Number(w.waste_pct ?? 0)),
              ),
            },
          )}
        </div>
      )}
    </Modal>
  );
}
